/**
 * Shared error trail — a single global interceptor for console.error,
 * window.onerror, and unhandled rejections.
 *
 * Both useAutoErrorReporter (auto-submits when rate spikes) and
 * FeedbackButton (attaches to manual bug reports) read from the same
 * trail.  Previously each had its own interceptor, which meant
 * console.error was overwritten twice and every error was processed
 * through two wrapper layers.
 */

export interface ErrorEntry {
  message: string;
  source?: string;
  ts: number;
}

const MAX_ERRORS = 20;

const trail: ErrorEntry[] = [];
let installed = false;

function push(message: string, source?: string) {
  trail.push({ message: message.slice(0, 500), source, ts: Date.now() });
  if (trail.length > MAX_ERRORS) trail.shift();
}

/**
 * Install the global interceptor exactly once.  Safe to call multiple
 * times (idempotent).
 */
export function installErrorInterceptor(): void {
  if (installed) return;
  installed = true;

  const originalConsoleError = console.error;

  console.error = (...args: unknown[]) => {
    push(args.map(String).join(' '), 'console.error');
    originalConsoleError.apply(console, args);
  };

  window.addEventListener('error', (event: ErrorEvent) => {
    push(event.message || String(event.error), event.filename || 'window.onerror');
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const msg = event.reason instanceof Error ? event.reason.message : String(event.reason);
    push(msg, 'unhandledrejection');
  });
}

/** Read a snapshot of recent errors (non-destructive). */
export function getErrorTrailSnapshot(): ErrorEntry[] {
  return trail.slice();
}

/** Read and clear the trail (used by auto-reporter after submitting). */
export function drainErrorTrail(): ErrorEntry[] {
  return trail.splice(0, trail.length);
}

/** Current length without copying. */
export function errorTrailLength(): number {
  return trail.length;
}

/**
 * Count errors within a time window.
 */
export function recentErrorCount(windowMs: number): number {
  const cutoff = Date.now() - windowMs;
  let count = 0;
  for (let i = trail.length - 1; i >= 0; i--) {
    if (trail[i].ts >= cutoff) count++;
    else break;
  }
  return count;
}
